/**
 * OverlayManager - Global controller to enforce mutual exclusion and priority
 * between overlays that must never be shown at the same time:
 * cart drawer, cookie consent, Klaviyo popup, chat widget, promo popup.
 *
 * Priority order (index 0 = highest). Opening an overlay force-closes every
 * currently-active overlay with *lower* priority via its registered close()
 * callback. Opening is blocked only while a *higher*-priority overlay is
 * active; the caller decides what to do in that case (the cart drawer, for
 * example, always opens regardless per product requirements, then relies on
 * this manager having evicted anything lower-priority).
 */
(function() {
  if (window.OverlayManager) return;

  const PANDECTES_HOST_SELECTOR = 'pandectes-cmp';
  const KLAVIYO_POPUP_SELECTOR = '[data-testid="POPUP"]';
  const KLAVIYO_CLOSE_SELECTOR = '.klaviyo-close-form, button[class*="needsclick"][aria-label*="Close"], button[class*="needsclick"][aria-label*="close"], button[class*="needsclick"][aria-label*="Fermer"], button[class*="needsclick"][title*="Close"], button[data-testid="form-close-button"]';
  const KLAVIYO_SCROLL_LOCK_SELECTOR = '[data-kl-scroll-locking-modal="true"], .klaviyo-modal-backdrop, div[class*="kl-modal-backdrop"], div[class*="kl-modal-overlay"], div[data-testid*="MODAL_OVERLAY"], div[class*="kl-modal"]:not([data-testid="POPUP"]):not(:has(form))';
  const NOVA_BANNER_SELECTOR = '#nova-currency-bar, .nova-currency-bar, .nova-multi-currency-bar, #nova-multi-currency-converter, div[class*="nova-currency"]';

  function isElementVisible(node) {
    return !!node && node.getClientRects().length > 0;
  }

  class OverlayManager {
    constructor() {
      this.priorityOrder = ['cart_drawer', 'menu_drawer', 'facet_drawer', 'cookie', 'klaviyo_popup', 'chat', 'promo_popup'];
      this.activeOverlays = new Set();
      this.registry = new Map();
      this.initScrollGuardian();
    }

    priorityIndex(name) {
      const index = this.priorityOrder.indexOf(name);
      return index === -1 ? this.priorityOrder.length : index;
    }

    /**
     * Register an overlay so the manager can force-close it (via its own
     * close handler, not just CSS) when a higher-priority overlay opens.
     * @param {string} name
     * @param {{ close: Function }} handlers
     */
    register(name, handlers) {
      this.registry.set(name, handlers);
    }

    /**
     * Whether `name` is currently blocked from opening by an active,
     * higher-priority overlay.
     * @param {string} name
     * @returns {boolean}
     */
    canOpen(name) {
      const requesterPriority = this.priorityIndex(name);
      for (const activeName of this.activeOverlays) {
        if (activeName === name) continue;
        if (this.priorityIndex(activeName) < requesterPriority) return false;
      }
      return true;
    }

    /**
     * Request to open an overlay. Always force-closes any active
     * lower-priority overlay. Returns false if a higher-priority overlay is
     * currently blocking it — the caller decides whether to proceed anyway.
     * @param {string} name
     * @returns {boolean}
     */
    requestOpen(name) {
      const allowed = this.canOpen(name);
      const requesterPriority = this.priorityIndex(name);

      Array.from(this.activeOverlays).forEach((activeName) => {
        if (activeName === name) return;
        if (name === 'cart_drawer' && activeName === 'menu_drawer') return;
        if (this.priorityIndex(activeName) > requesterPriority) {
          this.evict(activeName);
        }
      });

      this.activeOverlays.add(name);
      document.body.classList.add(`overlay-active--${name}`);
      document.body.classList.add('overlay-is-active');
      document.dispatchEvent(new CustomEvent('overlay:open', { detail: { name, active: Array.from(this.activeOverlays) } }));

      return allowed;
    }

    /**
     * Force-close a lower-priority overlay via its registered close()
     * handler (passing `forced = true` so it knows not to persist a
     * dismissal cookie), falling back to just clearing state if nothing is
     * registered for it.
     * @param {string} name
     */
    evict(name) {
      const handlers = this.registry.get(name);
      if (handlers && typeof handlers.close === 'function') {
        handlers.close(true);
      } else {
        this.close(name);
      }
    }

    /**
     * Mark an overlay as closed. Safe to call even if it wasn't active.
     * @param {string} name
     */
    close(name) {
      if (!this.activeOverlays.has(name)) return;

      this.activeOverlays.delete(name);
      document.body.classList.remove(`overlay-active--${name}`);

      if (this.activeOverlays.size === 0) {
        document.body.classList.remove('overlay-is-active');
      }

      this.forceUnlockScroll();
      document.dispatchEvent(new CustomEvent('overlay:close', { detail: { name, active: Array.from(this.activeOverlays) } }));
    }

    /**
     * Check if any overlay (or a specific one) is currently open.
     * @param {string} [name]
     * @returns {boolean}
     */
    isOpen(name) {
      if (name) return this.activeOverlays.has(name);
      return this.activeOverlays.size > 0;
    }

    /**
     * Force-unlock scroll on documentElement and body unless a recognized
     * modal drawer (cart, mobile menu, facets, or open modal-dialog) is active.
     */
    forceUnlockScroll() {
      const hasModalLock = this.activeOverlays.has('cart_drawer') ||
                           this.activeOverlays.has('menu_drawer') ||
                           this.activeOverlays.has('facet_drawer') ||
                           (document.body && (document.body.classList.contains('menu-mobile--open') ||
                                              document.body.classList.contains('mini-cart--open')));
      if (hasModalLock) return;

      const openDialog = document.querySelector('modal-dialog[open]');
      if (openDialog) return;

      const docEl = document.documentElement;
      const bodyEl = document.body;
      const lockClasses = ['overflow-hidden', 'pandectes-blocked', 'klaviyo-modal-open', 'klaviyo-prevent-body-scrolling', 'is-locked', 'noscroll', 'no-scroll', 'scroll-locked'];

      if (docEl) {
        lockClasses.forEach((cls) => docEl.classList.remove(cls));
        if (docEl.style.overflow === 'hidden' || docEl.style.overflowY === 'hidden') {
          docEl.style.removeProperty('overflow');
          docEl.style.removeProperty('overflow-y');
        }
        if (docEl.style.position === 'fixed') {
          docEl.style.removeProperty('position');
        }
      }

      if (bodyEl) {
        lockClasses.forEach((cls) => bodyEl.classList.remove(cls));
        if (bodyEl.style.overflow === 'hidden' || bodyEl.style.overflowY === 'hidden') {
          bodyEl.style.removeProperty('overflow');
          bodyEl.style.removeProperty('overflow-y');
        }
        if (bodyEl.style.position === 'fixed') {
          bodyEl.style.removeProperty('position');
          bodyEl.style.removeProperty('top');
          bodyEl.style.removeProperty('left');
          bodyEl.style.removeProperty('width');
        }
        if (bodyEl.style.touchAction === 'none') {
          bodyEl.style.removeProperty('touch-action');
        }
      }
    }

    /**
     * Background guardian to ensure external scripts (Klaviyo, Nova, Pandectes)
     * cannot silently lock scroll or freeze the viewport.
     */
    initScrollGuardian() {
      if (this._guardianInitialized) return;
      this._guardianInitialized = true;

      const guardianTick = () => {
        this.forceUnlockScroll();
        this.sanitizeNovaElements();
        purgeKlaviyoBackdrops();
      };

      const guardianObserver = new MutationObserver(() => {
        requestAnimationFrame(guardianTick);
      });

      if (document.documentElement) {
        guardianObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
      }

      if (document.body) {
        guardianObserver.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
      } else {
        document.addEventListener('DOMContentLoaded', () => {
          if (document.body) {
            guardianObserver.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
          }
          guardianTick();
        });
      }

      // Run periodically during early load to catch rapid injection by app embeds
      const interval = setInterval(guardianTick, 400);
      setTimeout(() => clearInterval(interval), 12000);
    }

    /**
     * Sanitizes Nova Multi-Currency elements so they never trap scroll or pointer events.
     */
    sanitizeNovaElements() {
      document.querySelectorAll(NOVA_BANNER_SELECTOR).forEach((el) => {
        const computed = getComputedStyle(el);
        if (computed.position === 'fixed') {
          if (el.offsetHeight >= window.innerHeight * 0.6) {
            el.style.setProperty('height', 'auto', 'important');
            el.style.setProperty('max-height', '200px', 'important');
          }
          el.style.setProperty('pointer-events', 'none', 'important');
          el.querySelectorAll('*').forEach((child) => {
            child.style.setProperty('pointer-events', 'auto', 'important');
          });
        }
      });
    }
  }

  window.OverlayManager = new OverlayManager();

  /**
   * Pandectes GDPR app-embed banner adapter. Pandectes is the cookie-consent
   * banner actually in use on this store (not the theme's own cookie-banner
   * section, which is disabled). It renders its <dialog> inside a *shadow
   * root* on <pandectes-cmp> (declarative shadow DOM) — document.querySelector
   * cannot see into that at all, and a MutationObserver on the light DOM
   * doesn't see mutations inside a separate shadow tree either. Both have to
   * go through the host element's `.shadowRoot` explicitly. Visibility is
   * read from the dialog's own `open` attribute (confirmed via Pandectes'
   * own CSS: `.dialog[open] { opacity: 1 }`, closed dialogs get the browser's
   * default `dialog:not([open]) { display: none }`).
   * Eviction hides the dialog with an inline style (never clicks
   * Accept/Decline for the user — that would fake a consent decision) and
   * un-hides it once it's allowed to show again.
   */
  window.OverlayManager.isPandectesActive = () => window.OverlayManager.isOpen('cookie');

  function getPandectesDialog() {
    const host = document.querySelector(PANDECTES_HOST_SELECTOR);
    if (!host || !host.shadowRoot) return null;
    return host.shadowRoot.querySelector('dialog');
  }

  let pandectesVisible = false;
  let pandectesHiddenByUs = false;

  function evictPandectes() {
    const dialog = getPandectesDialog();
    if (dialog) {
      dialog.style.setProperty('display', 'none', 'important');
      pandectesHiddenByUs = true;
    }
    // Mark the tier closed so it doesn't keep blocking lower-priority
    // overlays while hidden. Dispatched via close(), which fires
    // 'overlay:close' synchronously — the reshow listener below defers its
    // reaction by a tick so it doesn't race the eviction that's still in
    // progress (the overlay that triggered this hasn't been marked active yet).
    window.OverlayManager.close('cookie');
  }
  window.OverlayManager.register('cookie', { close: evictPandectes });

  function tryReshowPandectes() {
    if (!pandectesHiddenByUs) return;
    if (!window.OverlayManager.canOpen('cookie')) return;

    pandectesHiddenByUs = false;
    const dialog = getPandectesDialog();
    if (dialog) dialog.style.removeProperty('display');
    window.OverlayManager.requestOpen('cookie');
  }
  document.addEventListener('overlay:close', () => setTimeout(tryReshowPandectes, 0));

  function checkPandectesVisibility() {
    const dialog = getPandectesDialog();
    const isVisible = !!dialog && dialog.hasAttribute('open') && isElementVisible(dialog);

    if (isVisible && !pandectesVisible) {
      pandectesVisible = true;
      if (window.OverlayManager.canOpen('cookie')) {
        window.OverlayManager.requestOpen('cookie');
      } else {
        evictPandectes();
      }
    } else if (!isVisible && pandectesVisible) {
      pandectesVisible = false;
      if (!pandectesHiddenByUs) {
        // Genuinely dismissed (Accept/Decline actually clicked), not hidden by us.
        window.OverlayManager.close('cookie');
      }
    }
  }

  let pandectesShadowObserver = null;
  function attachPandectesShadowObserver() {
    if (pandectesShadowObserver) return;
    const host = document.querySelector(PANDECTES_HOST_SELECTOR);
    if (!host || !host.shadowRoot) return;

    pandectesShadowObserver = new MutationObserver(checkPandectesVisibility);
    pandectesShadowObserver.observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'class', 'style', 'data-open']
    });
    checkPandectesVisibility();
  }

  // The host element (and its declarative shadow root) is usually already
  // in the initial HTML, so try immediately; also watch the light DOM in
  // case it's ever inserted later (e.g. a delayed app-embed load).
  attachPandectesShadowObserver();
  const pandectesHostObserver = new MutationObserver(attachPandectesShadowObserver);
  pandectesHostObserver.observe(document.documentElement, { childList: true, subtree: true });

  /**
   * Klaviyo on-site popup adapter & backdrop neutralizer.
   */
  let klaviyoActive = false;
  let klaviyoHiddenByUs = false;
  let klaviyoCloseRetryTimer = null;

  function purgeKlaviyoBackdrops() {
    document.querySelectorAll(KLAVIYO_SCROLL_LOCK_SELECTOR).forEach((el) => {
      if (el.matches(KLAVIYO_POPUP_SELECTOR) || el.querySelector(KLAVIYO_POPUP_SELECTOR)) return;
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
    });
    window.OverlayManager.forceUnlockScroll();
  }

  function clickKlaviyoCloseButton() {
    const button = document.querySelector(KLAVIYO_CLOSE_SELECTOR);
    if (button) {
      button.click();
      return true;
    }
    return false;
  }

  function closeKlaviyoForm(forced = false) {
    if (forced) {
      klaviyoHiddenByUs = true;
    }

    document.querySelectorAll(KLAVIYO_POPUP_SELECTOR).forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
    });

    purgeKlaviyoBackdrops();

    if (!clickKlaviyoCloseButton() && !forced) {
      if (klaviyoCloseRetryTimer) clearInterval(klaviyoCloseRetryTimer);
      let retries = 0;
      klaviyoCloseRetryTimer = setInterval(() => {
        retries++;
        if (clickKlaviyoCloseButton() || retries >= 10) {
          clearInterval(klaviyoCloseRetryTimer);
          klaviyoCloseRetryTimer = null;
          purgeKlaviyoBackdrops();
        }
      }, 100);
    }
  }

  window.OverlayManager.register('klaviyo_popup', { close: closeKlaviyoForm });

  function checkKlaviyoVisibility() {
    const node = document.querySelector(KLAVIYO_POPUP_SELECTOR);
    const isVisible = isElementVisible(node);

    if (isVisible && !klaviyoActive) {
      if (window.OverlayManager.canOpen('klaviyo_popup')) {
        klaviyoActive = true;
        window.OverlayManager.requestOpen('klaviyo_popup');
      } else {
        closeKlaviyoForm(true);
      }
    } else if (!isVisible && klaviyoActive) {
      klaviyoActive = false;
      window.OverlayManager.close('klaviyo_popup');
      purgeKlaviyoBackdrops();
      setTimeout(purgeKlaviyoBackdrops, 350);
      setTimeout(purgeKlaviyoBackdrops, 600);
    } else if (!isVisible && !klaviyoActive) {
      // If Klaviyo is not active or visible, ensure backdrops never linger
      purgeKlaviyoBackdrops();
    }
  }

  document.addEventListener('overlay:close', (event) => {
    window.OverlayManager.forceUnlockScroll();
    purgeKlaviyoBackdrops();

    // If an overlay closed and Klaviyo was suppressed by priority (e.g. cookie banner closed):
    if (klaviyoHiddenByUs && window.OverlayManager.canOpen('klaviyo_popup')) {
      klaviyoHiddenByUs = false;
      document.querySelectorAll(KLAVIYO_POPUP_SELECTOR).forEach((el) => {
        el.style.removeProperty('display');
        el.style.removeProperty('pointer-events');
      });
      // Backdrops must remain purged so full-screen click-trapping never occurs
      purgeKlaviyoBackdrops();
      klaviyoActive = true;
      window.OverlayManager.requestOpen('klaviyo_popup');
    } else {
      purgeKlaviyoBackdrops();
    }

    setTimeout(checkKlaviyoVisibility, 50);
  });

  let klaviyoElementObserver = null;
  let klaviyoObservedNode = null;
  function attachKlaviyoElementObserver() {
    const node = document.querySelector(KLAVIYO_POPUP_SELECTOR);

    if (node && node !== klaviyoObservedNode) {
      if (klaviyoElementObserver) klaviyoElementObserver.disconnect();
      klaviyoElementObserver = new MutationObserver(checkKlaviyoVisibility);
      klaviyoElementObserver.observe(node, { attributes: true, attributeFilter: ['style', 'class'] });
      klaviyoObservedNode = node;
    }

    checkKlaviyoVisibility();
  }

  const klaviyoHostObserver = new MutationObserver(attachKlaviyoElementObserver);
  klaviyoHostObserver.observe(document.body, { childList: true, subtree: true });
  attachKlaviyoElementObserver();
})();
